import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Button, Icon, IconButton } from '@shopflo/ui'
import { CaretDown, PlusCircle, Tag, X } from '@phosphor-icons/react'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import useGTM from 'src/lib/hooks/useGTM'
import {
  CustomerTagRuleActionType,
  CustomerTagRuleOperatorType,
  CustomerTagRuleType,
  CustomerTagSegmentFormStateType
} from 'src/lib/types/segment'
import {
  createCustomerTagSegmentPayload,
  createSegment,
  editSegment,
  getSegmentById,
  parseCustomerTagSegmentConfig,
  parseCustomerTags
} from 'src/lib/utils/segmentsHelper'
import { generateUUID } from 'src/lib/utils/helpers'
import { constants, imagePaths } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import GenericSelectionModal from '../common/select-component/GenericSelectionModal'
import FormErrorText from '../common/FormErrorText'
import ImageContainer from '../common/ImageContainer'
import TagInput from './TagInput'
import SegmentDefinitionPanelSkeleton from './dynamic-creation-flow/SegmentDefinitionPanelSkeleton'
import {
  CONDITION_CONTAINER_PAD_HOVER_CLASS,
  FIELD_PILL_CLASS,
  PILL_INTERACTIVE_CLASS
} from './condition-classes'

interface CustomerTagSegmentCreationFlowProps {
  segmentId?: string | string[]
  isEditMode: boolean
  handleClose?: () => void
  onSuccess?: () => void
}

const CustomerTagSegmentCreationFlow: React.FC<CustomerTagSegmentCreationFlowProps> = ({
  segmentId,
  isEditMode,
  handleClose,
  onSuccess
}) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const { user } = useUser()
  const { t } = useLocale()
  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()

  const [formState, setFormState] = useState<CustomerTagSegmentFormStateType>({
    name: '',
    description: '',
    isActive: true,
    rules: [{ id: generateUUID(), action: 'INCLUDE', operator: 'OR', raw: '' }]
  })
  const [errors, setErrors] = useState<{ name: boolean; tags: boolean; nameShakeKey: number }>({
    name: false,
    tags: false,
    nameShakeKey: 0
  })
  const [activeRuleId, setActiveRuleId] = useState<string | null>(null)
  const [autoFocusRuleId, setAutoFocusRuleId] = useState<string | null>(null)
  /**
   * Shown until dismissed, then remembered. Starts `false` and is revealed on mount because
   * localStorage is unreadable during prerender — seeding from it would be a hydration mismatch.
   */
  const [isPromoVisible, setIsPromoVisible] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { name, description, rules } = formState

  const patchFormState = (patch: Partial<CustomerTagSegmentFormStateType>) =>
    setFormState(prev => ({ ...prev, ...patch }))

  const includeTagCount = useMemo(
    () =>
      rules.filter(rule => rule.action === 'INCLUDE').reduce((n, rule) => n + parseCustomerTags(rule.raw).length, 0),
    [rules]
  )

  const totalTagCount = useMemo(() => rules.reduce((n, rule) => n + parseCustomerTags(rule.raw).length, 0), [rules])

  useEffect(() => {
    if (!merchant?.accountId || !isEditMode || typeof segmentId !== 'string') return

    const loadSegment = async () => {
      try {
        setIsLoading(true)
        const response: any = await getSegmentById(merchant?.accountId, segmentId)
        const data = response?.data?.data ?? response?.data
        if (data) {
          setFormState(parseCustomerTagSegmentConfig(data))
          setErrors(prev => ({ ...prev, name: false, tags: false }))
        }
      } catch {
        errorToast(t('something_went_wrong'))
      } finally {
        setIsLoading(false)
      }
    }

    void loadSegment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant?.accountId, isEditMode, segmentId])

  useEffect(() => {
    setIsPromoVisible(globalThis?.window?.localStorage.getItem(constants.SHOPIFY_TAG_PROMO_DISMISSED) !== 'true')
  }, [])

  const dismissPromo = () => {
    setIsPromoVisible(false)
    globalThis?.window?.localStorage.setItem(constants.SHOPIFY_TAG_PROMO_DISMISSED, 'true')
  }

  const updateRule = (id: string, patch: Partial<CustomerTagRuleType>) => {
    setFormState(prev => ({
      ...prev,
      rules: prev.rules.map(rule => (rule.id === id ? { ...rule, ...patch } : rule))
    }))
    setActiveRuleId(id)
    if (errors.tags) setErrors(prev => ({ ...prev, tags: false }))
  }

  const addRule = () => {
    const next: CustomerTagRuleType = { id: generateUUID(), action: 'INCLUDE', operator: 'OR', raw: '' }
    setFormState(prev => ({ ...prev, rules: [...prev.rules, next] }))
    setActiveRuleId(next.id)
    setAutoFocusRuleId(next.id)
  }

  const removeRule = (id: string) => {
    setFormState(prev => ({
      ...prev,
      rules: prev.rules.length === 1 ? prev.rules : prev.rules.filter(rule => rule.id !== id)
    }))
  }

  const handleSave = async () => {
    const isNameMissing = !name.trim()
    const areTagsMissing = includeTagCount === 0

    if (isNameMissing || areTagsMissing) {
      setErrors(prev => ({
        name: isNameMissing || prev.name,
        tags: areTagsMissing || prev.tags,
        nameShakeKey: isNameMissing ? prev.nameShakeKey + 1 : prev.nameShakeKey
      }))
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    try {
      setIsSaving(true)
      const payload = createCustomerTagSegmentPayload(formState)

      const response: any =
        isEditMode && typeof segmentId === 'string'
          ? await editSegment('CUSTOMER_TAG', merchant?.accountId as string, segmentId, payload)
          : await createSegment('CUSTOMER_TAG', merchant?.accountId as string, payload)

      if (response?.error) {
        errorToast(response.error.message ? response.error.message : t('something_went_wrong'))
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
        return
      }

      const savedSegmentId = isEditMode ? (segmentId as string) : response?.data?.data?.id ?? response?.data?.id

      sendGTMTrigger(isEditMode ? 'segment_edited' : 'segment_created', 'click', {
        segmentType: 'CUSTOMER_TAG',
        tagCount: totalTagCount,
        operators: rules.map(rule => rule.operator).join(','),
        rowCount: rules.length
      })
      sendActivityLog({
        entityName: EntityName.SEGMENTS,
        actionName: t(isEditMode ? 'edited_segment' : 'created_segment'),
        metadata: {
          segment_id: savedSegmentId,
          segment_name: name,
          segment_inclusion_rules: rules.map(rule => ({
            action: rule.action,
            operator: rule.operator,
            values: parseCustomerTags(rule.raw)
          }))
        }
      })

      successToast(t(isEditMode ? 'segment_creation.segment_updated' : 'segment_creation.segment_created'))
      onSuccess?.()
      handleClose?.()
    } catch {
      errorToast(t('something_went_wrong'))
    } finally {
      setIsSaving(false)
    }
  }

  const showDefinitionSkeleton = isEditMode && isLoading
  const showNameError = errors.name && !name.trim()

  return (
    <div className='flex h-full flex-col'>
      {/* Close */}
      <div className='flex shrink-0 justify-end px-5 pt-4'>
        <IconButton IconComponent={X} size='sm' variant='outline' onClick={handleClose} />
      </div>

      <div className='relative min-h-0 flex-1'>
        <AnimatePresence mode='popLayout'>
          {showDefinitionSkeleton ? (
            <motion.div
              key='customer-tag-skeleton'
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
              transition={{ duration: 0 }}
              className='absolute inset-0 flex flex-col overflow-y-auto pt-6'
            >
              <SegmentDefinitionPanelSkeleton />
            </motion.div>
          ) : (
            <motion.div
              key='customer-tag-content'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
              exit={{ opacity: 1, transition: { duration: 0 } }}
              className='absolute inset-0 flex min-h-0 flex-col'
            >
              <div ref={scrollContainerRef} className='flex min-h-0 flex-1 flex-col overflow-y-auto pt-1'>
                <div className='flex flex-col'>
                  <div className='flex flex-col gap-2 px-8'>
                    <div className='flex items-center gap-1.5'>
                      <span className='flex shrink-0 grayscale'>
                        <ImageContainer
                          height={16}
                          imageSource={imagePaths.SHOPIFY_LOGO}
                          defaultImage={imagePaths.IMAGE_PLACEHOLDER}
                          altText={'Shopify'}
                        />
                      </span>
                      <span className='text-text-sm-medium text-zinc-500'>{t('segment_creation.shopify_tags')}</span>
                    </div>

                    <div className='flex flex-col gap-0.5 pb-4'>
                      <input
                        key={errors.nameShakeKey}
                        type='text'
                        placeholder={t('segment_creation.customer_tag_name_placeholder')}
                        value={name}
                        onChange={e => {
                          if (e.target.value.length > 36) return
                          patchFormState({ name: e.target.value })
                          if (errors.name) setErrors(prev => ({ ...prev, name: false }))
                        }}
                        autoFocus
                        className={`w-full border-none bg-transparent text-text-lg-semibold text-zinc-800 outline-none ${
                          showNameError ? 'animate-wiggle placeholder:text-red-300' : 'placeholder:text-zinc-400/70'
                        }`}
                      />
                      <input
                        type='text'
                        placeholder={t('segment_creation.description_placeholder')}
                        value={description}
                        onChange={e => {
                          if (e.target.value.length > 80) return
                          patchFormState({ description: e.target.value })
                        }}
                        className='w-full border-none bg-transparent text-text-sm-regular text-zinc-500 outline-none placeholder:text-zinc-400/70'
                      />
                      <div
                        className={`grid transition-all duration-200 ease-in-out ${
                          showNameError ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                        }`}
                      >
                        <div className='overflow-hidden'>
                          <FormErrorText message={t('segment_creation.name_required_error')} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Promo */}
                  {isPromoVisible && (
                    <div className='px-3'>
                      <div
                        className='flex flex-row items-start gap-2 overflow-hidden rounded-xl pr-3'
                        style={{
                          background:
                            'linear-gradient(90deg, #ECF3FF 0%, #ECF3FF 39.72%, var(--Core-zinc-100, #F4F4F5) 100%)'
                        }}
                      >
                        <div className='h-[100px]'>
                          <img src={imagePaths.SHOPIFY_TAG_PROMO_THUMBNAIL} />
                        </div>
                        <p className='flex-1 items-start py-6 text-text-xs-medium text-zinc-600'>
                          {t('segment_creation.shopify_tag_promo')}
                        </p>
                        <div className='pt-4'>
                          <IconButton
                            type='button'
                            IconComponent={X}
                            size='xs'
                            variant='ghost'
                            onClick={dismissPromo}
                            aria-label={t('segment_creation.dismiss')}
                            iconProps={{ weight: 'regular' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className='h-8 flex items-center' aria-hidden>
                  <div
                    style={{
                      width: '100%',
                      height: 1.5,
                      backgroundImage: 'radial-gradient(circle, #D4D4D8 0.75px, transparent 0.75px)',
                      backgroundSize: '6.5px 1.5px'
                    }}
                  />
                </div>

                {/* Conditions */}
                <div className='flex flex-col'>
                  {rules.map((rule, index) => (
                    <React.Fragment key={rule.id}>
                      {index > 0 ? <div className='mx-8 border-t border-zinc-950/5' aria-hidden /> : null}
                      <div className={`flex items-start gap-3 py-2.5 ${CONDITION_CONTAINER_PAD_HOVER_CLASS}`}>
                        <div className='min-w-[3.25rem] shrink-0'>
                          <div className='flex h-[22px] items-center'>
                            {index === 0 ? (
                              <span className='text-text-sm-medium text-zinc-500'>
                                {t('segment_creation.where_label')}
                              </span>
                            ) : (
                              <GenericSelectionModal
                                options={[
                                  { key: 'AND', value: t('segment_creation.operator_and') },
                                  { key: 'OR', value: t('segment_creation.operator_or') }
                                ]}
                                selectedKey={rule.operator}
                                onSelect={option =>
                                  updateRule(rule.id, { operator: option.key as CustomerTagRuleOperatorType })
                                }
                                placeholderComponent={
                                  <div className='flex cursor-pointer items-center gap-0.5 text-zinc-500/70 hover:text-zinc-500'>
                                    <span className='text-text-sm-medium'>
                                      {rule.operator === 'AND'
                                        ? t('segment_creation.operator_and')
                                        : t('segment_creation.operator_or')}
                                    </span>
                                    <Icon IconComponent={CaretDown} size='xxs' iconProps={{ weight: 'bold' }} />
                                  </div>
                                }
                                showSearch={false}
                                triggerClassName='!border-0 !outline-none'
                                contentClassName='w-32'
                              />
                            )}
                          </div>
                        </div>

                        <div className='flex min-w-0 flex-1 flex-col gap-2 pb-2'>
                          {/* Tags <operator> */}
                          <div className='flex flex-col'>
                            <div className='flex flex-row items-center justify-between gap-1'>
                              <div className='flex flex-row gap-1'>
                                <div className={FIELD_PILL_CLASS}>
                                  <Icon IconComponent={Tag} size='xs' className='text-zinc-400' />
                                  <span className='text-text-sm-medium text-zinc-600'>
                                    {t('segment_creation.tags')}
                                  </span>
                                </div>
                                <GenericSelectionModal
                                  options={[
                                    { key: 'INCLUDE', value: t('segment_creation.is') },
                                    { key: 'EXCLUDE', value: t('segment_creation.is_not') }
                                  ]}
                                  selectedKey={rule.action}
                                  onSelect={option =>
                                    updateRule(rule.id, { action: option.key as CustomerTagRuleActionType })
                                  }
                                  placeholderComponent={
                                    <div className={PILL_INTERACTIVE_CLASS}>
                                      {rule.action === 'EXCLUDE'
                                        ? t('segment_creation.is_not')
                                        : t('segment_creation.is')}
                                    </div>
                                  }
                                  showSearch={false}
                                  triggerClassName='!border-0 !outline-none'
                                  contentClassName='w-40'
                                />
                              </div>

                              <div className='flex h-[22px] shrink-0 items-center'>
                                <IconButton
                                  IconComponent={X}
                                  size='xs'
                                  variant='ghost'
                                  disabled={rules.length === 1}
                                  onClick={() => removeRule(rule.id)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className='flex flex-col gap-0'>
                            <TagInput
                              value={rule.raw}
                              onChange={value => updateRule(rule.id, { raw: value })}
                              variant='string'
                              compact
                              hideHint
                              autoFocus={autoFocusRuleId === rule.id}
                              onFocus={() => setActiveRuleId(rule.id)}
                              placeholder={t('segment_creation.tag_input_placeholder')}
                              error={errors.tags && rule.action === 'INCLUDE' && includeTagCount === 0}
                            />

                            {/* Collapse wrapper matches the name field / ManualSegmentCreationFlow */}
                            <div
                              className={`grid transition-all duration-200 ease-in-out ${
                                errors.tags && rule.action === 'INCLUDE' && includeTagCount === 0
                                  ? 'grid-rows-[1fr]'
                                  : 'grid-rows-[0fr]'
                              }`}
                            >
                              <div className='overflow-hidden'>
                                <FormErrorText message={t('segment_creation.tags_required_error')} />
                              </div>
                            </div>
                          </div>

                          {activeRuleId === rule.id && (
                            <ul className='flex list-disc flex-col gap-0.5 pl-4 py-0.5'>
                              <li className='text-text-xs-regular text-zinc-500'>
                                <span className='inline-flex items-center gap-1'>
                                  {t('segment_creation.tag_hint_comma_before')}
                                  <kbd className='inline-flex items-center justify-center gap-0.5 rounded-[4px] border border-zinc-950/24 px-1.5 h-4 text-[9px] leading-[10px] text-zinc-500'>
                                    ,
                                  </kbd>
                                  {t('segment_creation.tag_hint_comma_after')}
                                </span>
                              </li>
                              <li className='text-text-xs-regular text-zinc-500'>
                                {t('segment_creation.tag_hint_case')}
                              </li>
                              <li className='text-text-xs-regular text-zinc-500'>
                                {t('segment_creation.tag_hint_exact')}
                              </li>
                            </ul>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}

                  <div className='mx-8 border-t border-zinc-950/5 py-2.5'>
                    <Button
                      variant='dashed'
                      size='sm'
                      leftIcon={PlusCircle}
                      onClick={addRule}
                      className='h-7 w-fit bg-transparent text-text-sm-medium'
                    >
                      {t('segment_creation.add_condition')}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className='flex shrink-0 flex-col gap-1.5 px-5 pt-3 pb-4'>
        <Button
          id='customer-tag-segment-save-button'
          variant='primary'
          size='md'
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || user?.isDemoUser}
          className='w-full'
        >
          {t('segment_creation.save_segment')}
        </Button>
        <Button variant='ghost' size='md' onClick={handleClose} disabled={isSaving} className='w-full'>
          {t('segment_creation.cancel')}
        </Button>
      </div>
    </div>
  )
}

export default CustomerTagSegmentCreationFlow
